import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Building2, Store, Warehouse, Briefcase, UtensilsCrossed, Factory,
  CheckCircle, Download, FileText, ShieldCheck, PenTool,
  ChevronDown, ChevronUp, Calculator, ScrollText, AlertTriangle,
} from "lucide-react";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const PRICE = "9.99";

const PROPERTY_TYPES = [
  { icon: Briefcase,        title: "Office space",       desc: "Single suites, floors, or whole buildings: professional, medical, or creative.", tone: "gray" },
  { icon: Store,            title: "Retail storefronts", desc: "Shops, boutiques, and service businesses, including percentage-rent arrangements.", tone: "mint" },
  { icon: UtensilsCrossed,  title: "Restaurants & cafés",desc: "Food service space with specific use clauses and equipment provisions.", tone: "black" },
  { icon: Warehouse,        title: "Warehouse & storage",desc: "Distribution, storage, and logistics space with loading and access terms.", tone: "black" },
  { icon: Factory,          title: "Industrial & flex",  desc: "Light manufacturing, workshops, and mixed-use flex space.", tone: "gray" },
  { icon: Building2,        title: "Salons & studios",   desc: "Salons, gyms, studios, and other specialty commercial uses.", tone: "mint" },
];

const LEASE_TYPES = [
  { name: "Triple Net (NNN)",       who: "Tenant pays taxes, insurance, and CAM on top of base rent.",       note: "Most common for retail and standalone buildings." },
  { name: "Double Net (NN)",        who: "Tenant pays taxes and insurance; landlord handles structure.",     note: "A middle ground on expense sharing." },
  { name: "Single Net (N)",         who: "Tenant pays property taxes only.",                                  note: "Less common; landlord keeps most expenses." },
  { name: "Gross / Full Service",   who: "Tenant pays one flat rent; landlord covers operating costs.",       note: "Typical for multi-tenant office buildings." },
  { name: "Modified Gross",         who: "Rent includes some expenses; the rest are split.",                  note: "Negotiated case by case." },
  { name: "Percentage Lease",       who: "Base rent plus a percentage of gross sales above a breakpoint.",   note: "Standard in malls and high-traffic retail." },
];

const INCLUDES = [
  { icon: ScrollText,  title: "18 standard lease sections", desc: "Parties, premises, permitted use, term, rent, expenses, deposit, utilities, maintenance, alterations, insurance, indemnity, assignment, default, holdover, notices, and general provisions." },
  { icon: Calculator,  title: "Rent escalation built in",   desc: "Fixed-dollar or percentage annual increases, late fees, and grace periods." },
  { icon: ShieldCheck, title: "Personal guaranty option",   desc: "Add a guaranty page to hold an individual owner liable behind an LLC tenant." },
  { icon: FileText,    title: "Notary block",               desc: "Optional acknowledgment page with seal area for leases that need recording." },
  { icon: PenTool,     title: "Draw your signature",        desc: "Sign on screen with mouse, finger, or stylus: landlord, tenant, and guarantor." },
  { icon: Download,    title: "Instant PDF",                desc: "Multi-page, print-ready letter-size lease with page numbering." },
];

const STEPS = [
  { n: "1", title: "Enter the parties",   desc: "Landlord and tenant details, including entity type for business tenants." },
  { n: "2", title: "Describe the space",  desc: "Address, suite, square footage, parking, and exactly what the tenant may use it for." },
  { n: "3", title: "Set rent and terms",  desc: "Choose your lease type, base rent, escalation, deposit, and expense responsibilities." },
  { n: "4", title: "Sign and download",   desc: "Add signatures, download the PDF, and give each party an executed copy." },
];

const FAQS = [
  {
    q: "What is a commercial lease agreement?",
    a: "A commercial lease agreement is a legally binding contract between a landlord and a business tenant for the rental of property used for business purposes: office, retail, warehouse, restaurant, or industrial space. It sets out the rent, the length of the term, who pays which operating expenses, what the tenant may use the space for, and what happens if either side breaches.",
  },
  {
    q: "What's the difference between a triple net (NNN) and a gross lease?",
    a: "In a triple net lease, the tenant pays base rent plus their proportionate share of the three 'nets': property taxes, building insurance, and common area maintenance. In a gross or full-service lease, the tenant pays a single rent figure and the landlord absorbs those costs. NNN base rents look lower, but the tenant's total occupancy cost is often similar or higher once the nets are added, so always compare the fully loaded number.",
  },
  {
    q: "What does CAM mean?",
    a: "CAM stands for Common Area Maintenance, the cost of maintaining shared areas such as parking lots, lobbies, hallways, landscaping, snow removal, and exterior lighting. In net leases, each tenant pays a proportionate share, usually calculated from their square footage as a percentage of the building's leasable area.",
  },
  {
    q: "How long is a typical commercial lease?",
    a: "Commercial leases usually run three to ten years, considerably longer than residential leases. Retail and restaurant leases often run five to ten years because the tenant invests heavily in build-out. Shorter one to three year terms are common for small office suites. Renewal options let a tenant extend without renegotiating the whole lease.",
  },
  {
    q: "Should the lease include a personal guaranty?",
    a: "If your tenant is an LLC or corporation, the business entity may hold few assets, leaving the landlord with little recourse if it defaults. A personal guaranty makes an individual (usually the owner) personally responsible for the rent. It's standard for small businesses and startups. Well-capitalized tenants often negotiate it out or cap it at a set number of months' rent.",
  },
  {
    q: "Does a commercial lease need to be notarized?",
    a: "In most cases, no. A commercial lease is binding once both parties sign. Some states require notarization or recording for leases longer than a certain term (often one to three years), and lenders or title companies may ask for it during a financing or sale. The notary acknowledgment block in this generator is optional so you can add it when circumstances call for it.",
  },
  {
    q: "Do commercial tenants get the same protections as residential tenants?",
    a: "No, and this surprises many first-time business tenants. Most consumer and residential tenant protections simply don't apply to commercial leases. Courts generally treat both parties as sophisticated and enforce the written terms as agreed. That makes the precise wording of your lease far more consequential than in a residential rental.",
  },
  {
    q: "How much does this cost?",
    a: `A commercial lease agreement from MintSlip costs $${PRICE} as a one-time purchase, with no subscription required. You get a live preview while you build it and an instant, print-ready multi-page PDF. Attorneys typically charge several hundred to a few thousand dollars to draft a commercial lease from scratch.`,
  },
];

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

export default function CommercialLeaseLanding() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [heroRef, heroInView] = useInView();
  const [whatRef, whatInView] = useInView();
  const [typesRef, typesInView] = useInView();
  const [tableRef, tableInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [includesRef, includesInView] = useInView();
  const [pricingRef, pricingInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const go = () => navigate("/app/business-forms");

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>Commercial Lease Agreement Template | Create One Online for ${PRICE} | MintSlip</title>
        <meta name="description" content={`Create a commercial lease agreement online in minutes. NNN, gross, modified gross, and percentage leases for office, retail, warehouse, and restaurant space. Instant PDF for $${PRICE}.`} />
        <meta name="keywords" content="commercial lease agreement, commercial lease template, commercial rental agreement, NNN lease, triple net lease, gross lease, office lease, retail lease, warehouse lease, commercial lease form" />
        <meta property="og:title" content={`Commercial Lease Agreement Template | $${PRICE}`} />
        <meta property="og:description" content="Create a landlord-ready commercial lease in minutes. NNN, gross, and percentage lease types with CAM, guaranty, and notary blocks." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Commercial Lease Agreement Template | MintSlip" />
        <meta name="twitter:description" content="Office, retail, warehouse, and restaurant leases. Instant PDF download." />
        <link rel="canonical" href="https://mintslip.com/commercial-lease-agreement" />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "MintSlip Commercial Lease Agreement Generator",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": PRICE, "priceCurrency": "USD" },
          "description": "Create a commercial lease agreement online for office, retail, warehouse, restaurant, and industrial property.",
        })}</script>

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question", "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <Header />

      {/* HERO */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">
              <Building2 className="w-4 h-4 text-emerald-600" /> Office · Retail · Warehouse · Restaurant
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Commercial <span className="font-black">lease agreement</span>
          </h1>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Put your commercial tenancy in writing. Build a landlord-ready lease with the right rent structure,
            expense allocation, and protections, in minutes, not billable hours.
          </p>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your lease</span>
            </button>
          </div>
          <p className="text-center text-slate-500">
            One-time <span className="font-bold text-slate-900">${PRICE}</span> · Instant PDF download
          </p>

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mt-10 text-sm text-slate-500">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> NNN, gross &amp; percentage</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> Guaranty &amp; notary blocks</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> All 50 states</span>
          </div>
        </div>
      </section>

      {/* WHAT IT IS */}
      <section ref={whatRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <div className={`transition-all duration-700 ${whatInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-8">
            What is a <span className="font-black">commercial lease agreement</span>?
          </h2>
          <div className="max-w-3xl mx-auto text-slate-600 text-lg leading-relaxed space-y-4">
            <p>
              A <strong>commercial lease agreement</strong> is a binding contract between a property owner and a business tenant for
              space used to run a business: an office suite, a storefront, a warehouse, a restaurant, or a workshop.
            </p>
            <p>
              It's a very different animal from a residential lease. Terms run longer, the money is larger, and the parties negotiate
              things residential renters never see: who pays the property taxes, how common area costs get allocated, whether rent
              escalates each year, what happens to the tenant's build-out at the end of the term.
            </p>
            <p>
              Most importantly, <strong>commercial tenants don't get residential tenant protections.</strong> Courts assume both sides
              are sophisticated businesses and enforce the lease as written. That puts real weight on getting the document right.
            </p>
          </div>
        </div>
      </section>

      {/* PROPERTY TYPES */}
      <section ref={typesRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${typesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Works for <span className="font-black">any commercial space</span>
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${typesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          One generator, configured for the property type and rent structure you're actually using.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROPERTY_TYPES.map(({ icon: Icon, title, desc, tone }, i) => {
            const t = toneCard[tone];
            return (
              <div
                key={title}
                className={`rounded-[28px] ${t.bg} p-8 transition-all duration-700 ${typesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-xl md:text-2xl font-medium mb-3 ${t.big}`}>{title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* LEASE TYPES TABLE */}
      <section ref={tableRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Which <span className="font-black">lease type</span> do you need?
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          The lease type decides who pays operating expenses. Pick one and the generator writes the matching clause.
        </p>

        <div className={`overflow-x-auto bg-white border border-slate-200 rounded-2xl shadow-sm transition-all duration-700 delay-150 ${tableInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <table className="w-full text-left" style={{ minWidth: 620 }}>
            <thead>
              <tr className="bg-slate-50">
                <th className="p-4 text-sm font-semibold text-slate-700">Lease type</th>
                <th className="p-4 text-sm font-semibold text-slate-700">Who pays what</th>
                <th className="p-4 text-sm font-semibold text-slate-700">Typically used for</th>
              </tr>
            </thead>
            <tbody>
              {LEASE_TYPES.map((t, i) => (
                <tr key={t.name} className={i % 2 ? "bg-slate-50/50" : "bg-white"}>
                  <td className="p-4 text-sm font-semibold text-slate-900 whitespace-nowrap">{t.name}</td>
                  <td className="p-4 text-sm text-slate-600">{t.who}</td>
                  <td className="p-4 text-sm text-slate-500">{t.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section ref={stepsRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          How it <span className="font-black">works</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-7 text-center transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <div className="w-14 h-14 rounded-full bg-emerald-500 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">{s.n}</div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className={`flex justify-center mt-12 transition-all duration-700 delay-300 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <button className="navbar-cta" onClick={go}>
            <FileText size={18} />
            <span>Start your lease</span>
          </button>
        </div>
      </section>

      {/* INCLUDES */}
      <section ref={includesRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${includesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          What's <span className="font-black">included</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {INCLUDES.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-7 transition-all duration-700 ${includesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <span className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
                <Icon className="w-7 h-7 text-white" />
              </span>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-1">{title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section ref={pricingRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Simple <span className="font-black">pricing</span>
        </h2>
        <div className={`max-w-md mx-auto rounded-[28px] bg-[#0b0b0b] p-8 md:p-10 text-center transition-all duration-700 delay-150 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-6">
            <span className="font-display text-5xl font-black text-white">${PRICE}</span>
            <span className="text-slate-300 ml-2">one-time</span>
          </div>
          <ul className="text-left space-y-3 mb-8">
            {[
              "Full multi-page commercial lease",
              "Six lease types including NNN and percentage",
              "Rent escalation, CAM, and late fee terms",
              "Optional personal guaranty and notary blocks",
              "Live preview and instant PDF download",
            ].map(t => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-300">{t}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your lease</span>
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-4">No subscription required. Secured by Stripe.</p>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {FAQS.map((f, i) => (
            <div
              key={f.q}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${100 + i * 60}ms` }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900">{f.q}</span>
                {openFaq === i ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              </button>
              {openFaq === i && <div className="px-5 pb-5 text-slate-600 leading-relaxed text-sm">{f.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section ref={ctaRef} className="pb-16 md:pb-24 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Get it <span className="font-black">in writing</span> before the keys change hands
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            A clear lease prevents the disputes that cost landlords and tenants far more than the document ever will.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your commercial lease</span>
            </button>
          </div>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="pb-10 px-6 bg-white">
        <div className="max-w-3xl mx-auto p-5 bg-amber-50 border border-amber-200 rounded-2xl flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Disclaimer:</strong> MintSlip provides a self-service document preparation tool and is not a law firm. Nothing on
            this page is legal advice, and using this generator does not create an attorney–client relationship. Commercial leases
            create substantial long-term financial obligations and are governed by state-specific law. For high-value, long-term, or
            complex leases, have a licensed attorney in your state review the document before signing.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
